import { Omit } from '@/types';
import React, {
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  useEffect,
  useState,
} from 'react';

type NativeTextInputProps =
  | React.DetailedHTMLProps<
      InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >
  | React.DetailedHTMLProps<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      HTMLTextAreaElement
    >;

export type DebouncedInputProps = Omit<NativeTextInputProps, 'onChange'> & {
  type?: InputHTMLAttributes<HTMLInputElement>['type'];
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
  textArea?: boolean;
};

export const DebouncedInput: React.FC<DebouncedInputProps> = ({
  value: initialValue,
  onChange,
  debounce = 1000,
  textArea = false,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  if (textArea) {
    const textAreaProps = props as React.DetailedHTMLProps<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      HTMLTextAreaElement
    >;
    return (
      <textarea
        {...textAreaProps}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    );
  }

  const inputProps = props as React.DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
  return (
    <input
      {...inputProps}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
};
